import { useCallback, useMemo } from 'react';

import { 
  CodeType, 
  SearchCallbacks as UseCodeSearchCallbacks,
  ItemDto
} from 'src/types/base';
// ⚠️ 주석: validate 함수들은 더 이상 사용하지 않음 (categories.ts에서 처리)

// 검색 설정 import
import { getApiConfig } from 'src/utils/categoryUtils';

// ===== 타입 정의 =====

/**
 * executeSearch 함수의 옵션 타입
 * @template T - 검색 결과의 타입
 */
export interface ExecuteSearchOptions<T = any> {
  /** 검색 필터 (각 카테고리별로 다름. 예: { itemAccount: '100,200' }) */
  filters?: Record<string, any>;
  /** 정확한 코드 매칭 여부 (true: 단일 항목 반환, false: 리스트 반환) */
  exactMatch?: boolean;
  /** 검색 성공 시 콜백 함수 */
  onSuccess?: (item: T) => void;
  /** 자동 정확 매칭 여부 (결과가 1개면 자동 선택) */
  autoExactMatch?: boolean;
}

//모달 기능 훅
export const useCodeSearch = (callbacks?: UseCodeSearchCallbacks) => {
  const { onExactMatch, onNoMatch, onError } = callbacks || {};

  // ===== 성능 최적화: API 설정 캐시 =====
  const apiConfigCache = useMemo(() => new Map<CodeType, any>(), []);

  // ===== 공통 검색 로직 =====
  
  /**
   * 공통 검색 로직을 처리하는 함수
   * @template T - 검색 결과의 타입
   * @param type - 검색할 카테고리 타입 (item, customer, workplace 등)
   * @param searchText - 검색어
   * @param options - 검색 옵션 (필터, 정확 매칭 여부 등)
   * @returns 검색 결과 (정확 매칭: 단일 항목 또는 null, 리스트: 배열 또는 null)
   */
  const executeSearch = useCallback(async <T = any>(
    type: CodeType,
    searchText: string,
    options: ExecuteSearchOptions<T> = {}
  ): Promise<T | T[] | null> => {
    const { 
      filters = {}, 
      exactMatch = false, 
      onSuccess, 
      autoExactMatch = false
    } = options;
    
    // 필터는 그대로 전달 (categories.ts에서 변환)
    const validatedFilters = filters;
    
    // 검색 설정 가져오기 (캐시 적용)
    let config = apiConfigCache.get(type);
    if (!config) {
      config = getApiConfig(type);
      if (!config) {
        console.warn(`Unsupported code type: ${type}`);
        return [];
      }
      apiConfigCache.set(type, config);
    }
    const primaryCodeField = Array.isArray(config.codeField)
      ? config.codeField[0]
      : config.codeField;
    
    // 정확한 매칭 모드에서 빈 값 처리
    if (exactMatch && (!searchText || !searchText.toString().trim())) {
      return null;
    }
    
    const inputCd = exactMatch ? searchText.toString().trim().toUpperCase() : searchText;
    
    try {
      // API 파라미터 생성
      const apiParams = config.getApiParams(inputCd, validatedFilters);
      
      // API 호출
      const response = await config.apiFunction(...apiParams);
      let data = response.data.data;
      
      // 응답 데이터 변환 (필요한 경우)
      if (config.transformResponse) {
        data = config.transformResponse(data);
      }
      
      // 정확한 매칭 모드
      if (exactMatch) {
        const foundItem = data.find((item: any) => 
          item[primaryCodeField]?.trim().toUpperCase() === inputCd
        );
        
        if (foundItem) {
          onSuccess?.(foundItem as T);
          onExactMatch?.(foundItem, type);
          return foundItem as T;
        } else {
          onNoMatch?.(inputCd, type);
          return null;
        }
      }
      
      // 자동 정확한 매칭 처리
      if (autoExactMatch && data.length === 1) {
        const singleItem = data[0];
        const isExactMatch = singleItem[primaryCodeField]?.trim().toUpperCase() === searchText.toString().trim().toUpperCase();
        
        if (isExactMatch) {
          onSuccess?.(singleItem as T);
          onExactMatch?.(singleItem, type);
          return singleItem as T;
        }
      }
      
      // 일반적인 모달용 검색 (배열 반환)
      return data as T[];
    } catch (error) {
      console.error(`Failed to search ${type}:`, error);
      onError?.(error, type);
      
      if (exactMatch) {
        onNoMatch?.(inputCd, type);
        return null;
      }
      
      return [];
    }
  }, [onExactMatch, onNoMatch, onError, apiConfigCache]);

  /**
   * 공통 모달용 검색 함수
   * ⚠️ 필터 검증 로직 제거: categories.ts의 getApiParams에서 직접 처리
   */
  const searchForModalByType = useCallback(async (
    type: CodeType,
    searchText: string = 'all',
    filters: any = {}
  ) => {
    // 필터는 그대로 전달 (categories.ts에서 변환 처리)
    const result = await executeSearch(type, searchText, {
      filters
    });
    return Array.isArray(result) ? result : [];
  }, [executeSearch]);



  // 공통 테이블용 정확한 검색 함수 (모든 카테고리 지원)
  // ⚠️ 필터 검증 로직 제거: categories.ts의 getApiParams에서 직접 처리
  const searchExactCode = useCallback(async <T = any>(
    type: CodeType,
    code: string,
    onSuccess?: (item: T) => void
  ) => {
    return await executeSearch<T>(type, code, {
      exactMatch: true,
      onSuccess
    });
  }, [executeSearch]);

    // ===== 기존 호환성 함수들 =====

  // 기존 호환성을 위한 품목 전용 래퍼 함수
  const searchExactItemCode = useCallback(async (
    itemCode: string,
    onSuccess?: (item: ItemDto) => void
  ) => {
    return await searchExactCode<ItemDto>('item', itemCode, onSuccess);
  }, [searchExactCode]);

  return {
    // 모달용 검색 (주요 기능)
    searchForModalByType,
    // 공통 정확 검색 (모든 카테고리 지원)
    searchExactCode,
    // 상세 테이블용 정확 검색 (기존 호환성)
    searchExactItemCode, // PUR은 일단 이거 사용중 이후 수정 가능
    // 필터를 지원하는 공통 검색 (executeSearch 직접 노출)
    executeSearch
  };
};
